/**
 * DRE gerencial (ADR 0049, PR-6; decisão 14): subtotais e diferenças, em decimal exato. A
 * estrutura vem do mapeamento gerencial vigente (seções, linhas XX.XX, Resultado × Fora do
 * resultado); o sinal de cada seção é dado do mapeamento, nunca presumido pelo sistema.
 */
import { z } from 'zod';
import { costCenterScopeFields } from './cost-center.ts';
import { fromScaled, toScaled } from './money.ts';
import { localizedText } from './schemas.ts';
import { FPA_ACTUAL_VIEWS } from './fpa-ajuste.ts';

const uuid = z.string().uuid();

export interface FpaPlSectionLike {
  id: string;
  block: 'result' | 'outside';
  isSubtotal: boolean;
  /** 1 soma, -1 subtrai; null = a definir. */
  sign: 1 | -1 | null;
}

/**
 * Subtotais da DRE: cada subtotal é a soma, com sinal, das seções do bloco Resultado que vêm antes
 * dele (na ordem do mapeamento). Seção com valores e sem sinal deixa pendentes (null) os subtotais
 * seguintes: o sistema não adivinha se ela soma ou subtrai. Seções fora do resultado não entram.
 */
export function plSubtotals(
  sections: readonly FpaPlSectionLike[],
  values: ReadonlyMap<string, Readonly<Record<string, string>>>,
): Map<string, Record<string, string> | null> {
  const out = new Map<string, Record<string, string> | null>();
  const acc = new Map<string, bigint>();
  let pending = false;
  for (const s of sections) {
    if (s.block !== 'result') continue;
    if (s.isSubtotal) {
      out.set(
        s.id,
        pending ? null : Object.fromEntries([...acc].map(([m, v]) => [m, fromScaled(v)])),
      );
      continue;
    }
    const v = values.get(s.id) ?? {};
    const hasValues = Object.values(v).some((x) => toScaled(x) !== 0n);
    if (!s.sign) {
      if (hasValues) pending = true;
      continue;
    }
    for (const [m, x] of Object.entries(v)) {
      acc.set(m, (acc.get(m) ?? 0n) + BigInt(s.sign) * toScaled(x));
    }
  }
  return out;
}

/** Soma dos meses de uma linha ("" ou nada = sem dados). */
export function sumMonths(months: Readonly<Record<string, string>>): string | null {
  const vals = Object.values(months);
  if (!vals.length) return null;
  return fromScaled(vals.reduce((s, v) => s + toScaled(v), 0n));
}

// ---------------------------------------------------------------------------
// Estrutura da DRE em níveis (PR-7b): grupos, subtotais e indicadores configuráveis
// ---------------------------------------------------------------------------

export const FPA_PL_NODE_KINDS = ['group', 'subtotal', 'indicator'] as const;
export type FpaPlNodeKind = (typeof FPA_PL_NODE_KINDS)[number];

const nodeFields = {
  parentId: uuid.nullish().transform((v) => v ?? null),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9._-]{0,19}$/, 'Use até 20 letras maiúsculas, números, ., - ou _'),
  name: localizedText,
  block: z.enum(['result', 'outside']).default('result'),
  numeratorId: uuid.nullish().transform((v) => v ?? null),
  denominatorId: uuid.nullish().transform((v) => v ?? null),
  sortOrder: z.number().int().min(1).max(9999).default(100),
  notes: z
    .string()
    .trim()
    .max(1000)
    .nullish()
    .transform((v) => (v ? v : null)),
  /** Grupo: as linhas XX.XX somadas por ele. */
  lineIds: z.array(uuid).max(500).default([]),
  /** Subtotal: os itens (grupos ou subtotais) somados por ele. */
  termIds: z.array(uuid).max(100).default([]),
};
const checkNode = (
  v: {
    kind: FpaPlNodeKind;
    parentId: string | null;
    numeratorId: string | null;
    denominatorId: string | null;
    lineIds: string[];
    termIds: string[];
    block: 'result' | 'outside';
  },
  ctx: z.RefinementCtx,
) => {
  const issue = (path: string, message: string) =>
    ctx.addIssue({ code: 'custom', path: [path], message });
  if (v.kind !== 'group' && v.parentId) issue('parentId', 'Só grupos ficam dentro de outro grupo');
  if (v.kind !== 'group' && v.lineIds.length) issue('lineIds', 'Só grupos recebem linhas');
  if (v.kind !== 'subtotal' && v.termIds.length) issue('termIds', 'Só subtotais somam itens');
  if (v.kind === 'subtotal' && !v.termIds.length) issue('termIds', 'Escolha os itens somados');
  if (v.kind === 'indicator') {
    if (!v.numeratorId) issue('numeratorId', 'Escolha o numerador');
    if (!v.denominatorId) issue('denominatorId', 'Escolha o denominador');
    if (v.block !== 'result') issue('block', 'Indicadores ficam no Resultado');
  } else if (v.numeratorId || v.denominatorId) {
    issue('numeratorId', 'Só indicadores têm numerador e denominador');
  }
  if (v.kind !== 'group' && v.block !== 'result') issue('block', 'Fora do resultado só há grupos');
};
export const fpaPlNodeInput = z
  .object({ mappingVersionId: uuid, kind: z.enum(FPA_PL_NODE_KINDS), ...nodeFields })
  .superRefine(checkNode);
export type FpaPlNodeInput = z.infer<typeof fpaPlNodeInput>;
/** Edição: o tipo e o mapeamento não mudam (o tipo vem do item gravado, para conferir as regras). */
export const fpaPlNodeUpdateInput = z
  .object({ kind: z.enum(FPA_PL_NODE_KINDS), ...nodeFields })
  .superRefine(checkNode);

export interface FpaPlNodeLike {
  id: string;
  parentId: string | null;
  kind: FpaPlNodeKind;
  numeratorId: string | null;
  denominatorId: string | null;
  termIds: readonly string[];
  lineIds: readonly string[];
}
type Months = Record<string, string>;
/**
 * Por que o valor do item não é mostrado. "restricted": depende de linhas de pessoal que a pessoa
 * não vê (sigilo, ADR 0049 R14; v3.1 de 23/10/2026): nunca é calculado só com o que é visível.
 */
export type FpaPlUnavailable = 'pending' | 'outside_structure' | 'restricted';
export interface FpaPlNodeValue {
  /** Grupos e subtotais: efeito no resultado; indicadores: % (4 casas). Null = indisponível. */
  months: Months | null;
  total: string | null;
  /** Meses (e o total) sem denominador positivo: o indicador fica "n/d" nesses pontos. */
  notAvailable?: string[];
  unavailable: FpaPlUnavailable | null;
  /**
   * Grupo restrito: soma só das linhas visíveis ("Total visível"), fora da cadeia de subtotais e
   * de qualquer indicador. Ausente quando nenhuma linha visível tem valor (nunca vira zero).
   */
  visibleMonths?: Months;
  visibleTotal?: string | null;
}

/** Linhas (XX.XX) cujo valor pode estar oculto para a pessoa, na visão sem pessoal. */
export interface FpaPlRestriction {
  /** Linhas de pessoal: Tipo de Gasto ou Classificação na lista de pessoal. */
  protectedLineIds: ReadonlySet<string>;
  /**
   * Linhas de seções que podem receber pessoal sem linha própria (Tipo de Gasto protegido ou com
   * classificação de pessoal permitida): um valor oculto ali ficaria fora da estrutura sem que a
   * pessoa soubesse, então subtotais e indicadores que as somam também ficam restritos.
   */
  uncertainLineIds: ReadonlySet<string>;
}

const sumInto = (acc: Map<string, bigint>, months: Months) => {
  for (const [m, v] of Object.entries(months)) acc.set(m, (acc.get(m) ?? 0n) + toScaled(v));
};
const toMonths = (acc: Map<string, bigint>): Months =>
  Object.fromEntries(
    [...acc].sort(([a], [b]) => a.localeCompare(b)).map(([m, v]) => [m, fromScaled(v)]),
  );

/** Percentual exato (4 casas, metade para longe do zero): num ÷ den × 100. */
export function percentOf(num: string, den: string): string | null {
  const d = toScaled(den);
  if (d <= 0n) return null;
  const n = toScaled(num) * 100n * 10000n;
  const negative = n < 0n;
  const an = negative ? -n : n;
  const q = (an * 2n + d) / (2n * d);
  return fromScaled(negative ? -q : q);
}

/**
 * Valores da estrutura da DRE (PR-7b). `lineEffects` traz o efeito de cada linha XX.XX no resultado
 * (valor × sinal): `null` quando o sinal está pendente. Grupo = linhas + grupos filhos; subtotal =
 * soma dos itens; indicador = numerador ÷ denominador × 100, mês a mês e no ano, "n/d" quando o
 * denominador não é positivo. Nada é presumido: item pendente deixa pendente quem depende dele;
 * `outsideStructure` (valores do Resultado fora da estrutura) deixa pendentes os subtotais e
 * indicadores. Na visão sem pessoal (`restriction`), a dependência vem da estrutura, não dos
 * dados: grupo com linha de pessoal, e subtotal ou indicador que soma linha de seção que pode ter
 * pessoal, ficam "restricted" (sem valor), mesmo sem lançamento no recorte; os independentes
 * continuam calculados.
 */
export function plStructure(
  nodes: readonly FpaPlNodeLike[],
  lineEffects: ReadonlyMap<string, Months | null>,
  opts: { outsideStructure?: boolean; restriction?: FpaPlRestriction | null } = {},
): Map<string, FpaPlNodeValue> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  // Linhas de cada item, em qualquer nível (grupos filhos, termos, numerador e denominador).
  const linesMemo = new Map<string, Set<string>>();
  const linesOf = (id: string): Set<string> => {
    const known = linesMemo.get(id);
    if (known) return known;
    const n = byId.get(id);
    const out = new Set<string>(n?.lineIds ?? []);
    linesMemo.set(id, out);
    if (!n) return out;
    const parts = [
      ...nodes.filter((c) => c.parentId === n.id).map((c) => c.id),
      ...n.termIds,
      ...(n.numeratorId ? [n.numeratorId] : []),
      ...(n.denominatorId ? [n.denominatorId] : []),
    ];
    for (const p of parts) for (const l of linesOf(p)) out.add(l);
    return out;
  };
  const r = opts.restriction;
  // Grupo: tem linha de pessoal. Subtotal: soma linha de pessoal ou de seção que pode ter pessoal
  // sem linha própria (o valor oculto ficaria fora da estrutura). Indicador: pelo numerador e
  // pelo denominador.
  const restrictedMemo = new Map<string, boolean>();
  const restricted = (n: FpaPlNodeLike): boolean => {
    if (!r) return false;
    const known = restrictedMemo.get(n.id);
    if (known !== undefined) return known;
    restrictedMemo.set(n.id, false);
    const ls = [...linesOf(n.id)];
    const out =
      n.kind === 'indicator'
        ? [n.numeratorId, n.denominatorId].some((id) => !!id && restricted(byId.get(id)!))
        : n.kind === 'group'
          ? ls.some((l) => r.protectedLineIds.has(l))
          : ls.some((l) => r.protectedLineIds.has(l) || r.uncertainLineIds.has(l));
    restrictedMemo.set(n.id, out);
    return out;
  };
  const memo = new Map<string, Months | null | 'outside'>();
  const visiting = new Set<string>();
  const value = (id: string): Months | null | 'outside' => {
    if (memo.has(id)) return memo.get(id)!;
    const n = byId.get(id);
    if (!n || visiting.has(id))
      throw new Error('Estrutura da DRE inválida (ciclo ou item ausente)');
    visiting.add(id);
    let out: Months | null | 'outside' = null;
    if (n.kind === 'group') {
      const acc = new Map<string, bigint>();
      let pending = false;
      for (const l of n.lineIds) {
        const e = lineEffects.get(l);
        if (e === null) pending = true;
        else if (e) sumInto(acc, e);
      }
      for (const child of nodes.filter((c) => c.parentId === n.id)) {
        const v = value(child.id);
        if (v === null || v === 'outside') pending = true;
        else sumInto(acc, v);
      }
      out = pending ? null : toMonths(acc);
    } else if (n.kind === 'subtotal') {
      const acc = new Map<string, bigint>();
      let state: null | 'outside' | 'ok' = 'ok';
      for (const t of n.termIds) {
        const v = value(t);
        if (v === null) state = null;
        else if (v === 'outside') state = state === null ? null : 'outside';
        else sumInto(acc, v);
      }
      if (state === 'ok' && opts.outsideStructure) state = 'outside';
      out = state === 'ok' ? toMonths(acc) : state;
    }
    visiting.delete(id);
    memo.set(id, out);
    return out;
  };
  const result = new Map<string, FpaPlNodeValue>();
  const total = (m: Months) => sumMonths(m);
  for (const n of nodes) {
    if (restricted(n)) {
      // Sem valor: nem o resultado sem pessoal, nem zero. Grupo mostra à parte o "Total visível".
      const v = n.kind === 'group' ? value(n.id) : null;
      const visible = v && v !== 'outside' && Object.keys(v).length ? v : null;
      result.set(n.id, {
        months: null,
        total: null,
        unavailable: 'restricted',
        ...(visible ? { visibleMonths: visible, visibleTotal: total(visible) } : {}),
      });
      continue;
    }
    if (n.kind !== 'indicator') {
      const v = value(n.id);
      result.set(n.id, {
        months: v === null || v === 'outside' ? null : v,
        total: v === null || v === 'outside' ? null : total(v),
        unavailable: v === null ? 'pending' : v === 'outside' ? 'outside_structure' : null,
      });
      continue;
    }
    const num = value(n.numeratorId!);
    const den = value(n.denominatorId!);
    if (num === null || den === null || num === 'outside' || den === 'outside') {
      result.set(n.id, {
        months: null,
        total: null,
        unavailable: num === null || den === null ? 'pending' : 'outside_structure',
      });
      continue;
    }
    const months: Months = {};
    const notAvailable: string[] = [];
    for (const m of new Set([...Object.keys(num), ...Object.keys(den)])) {
      const pct = den[m] ? percentOf(num[m] ?? '0', den[m]!) : null;
      if (pct === null) notAvailable.push(m);
      else months[m] = pct;
    }
    const tn = total(num);
    const td = total(den);
    const pctTotal = td ? percentOf(tn ?? '0', td) : null;
    if (pctTotal === null) notAvailable.push('total');
    result.set(n.id, {
      months,
      total: pctTotal,
      notAvailable: notAvailable.sort(),
      unavailable: null,
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Visões da DRE (PR-7c): mês escolhido, acumulado até ele e ano, alinhados pelo mês do ano
// ---------------------------------------------------------------------------

/** Mês do ano ("01" a "12") de uma competência AAAA-MM-DD ou AAAA-MM. */
export const monthOfYear = (month: string) => month.slice(5, 7);

/**
 * Valor do mês do ano escolhido ("03" = março), em qualquer exercício: assim o Forecast 2027
 * compara com a Base de Referência 2026 mês a mês. Sem lançamento: null (sem dados não é zero).
 */
export function valueAtMonth(months: Readonly<Record<string, string>>, mm: string): string | null {
  const hit = Object.entries(months).filter(([m]) => monthOfYear(m) === mm);
  if (!hit.length) return null;
  return fromScaled(hit.reduce((s, [, v]) => s + toScaled(v), 0n));
}

/** Acumulado do ano até o mês escolhido (inclusive). Sem nenhum lançamento até lá: null. */
export function accumulatedUntil(
  months: Readonly<Record<string, string>>,
  mm: string,
): string | null {
  const hit = Object.entries(months).filter(([m]) => monthOfYear(m) <= mm);
  if (!hit.length) return null;
  return fromScaled(hit.reduce((s, [, v]) => s + toScaled(v), 0n));
}

/** Consulta da DRE: a versão, a comparação opcional, a empresa e o escopo de centros. */
/** Moedas em que a DRE e as comparações podem ser apresentadas (o lançamento não muda). */
export const FPA_PRESENTATION_CURRENCIES = ['BRL', 'USD'] as const;
export type FpaPresentationCurrency = (typeof FPA_PRESENTATION_CURRENCIES)[number];

export const fpaPlReportQuery = z.object({
  versionId: uuid,
  /** Moeda de apresentação; sem ela, a da versão. */
  currency: z.enum(FPA_PRESENTATION_CURRENCIES).optional(),
  /** Realizado ajustado (padrão) ou o original do NetSuite (ADR 0032, 10.5). */
  view: z.enum(FPA_ACTUAL_VIEWS).default('adjusted'),
  compareVersionId: uuid.optional(),
  legalEntityId: uuid.optional(),
  /** Unidade de negócio (PR-7c). */
  businessUnitId: uuid.optional(),
  ...costCenterScopeFields,
});

/** Títulos da planilha da DRE, já traduzidos pela tela (PR-7d). */
const plLabel = z.string().trim().min(1).max(120);
/** Exportação Excel da DRE: a mesma consulta da tela, o mês do acumulado e os títulos. */
export const fpaPlExportInput = fpaPlReportQuery.extend({
  /** Idioma dos nomes dos itens da estrutura. */
  lang: z.enum(['pt-BR', 'en']).default('pt-BR'),
  month: z
    .string()
    .regex(/^(0[1-9]|1[0-2])$/)
    .default('12'),
  labels: z.object({
    sheetMonths: plLabel,
    sheetPeriod: plLabel,
    item: plLabel,
    total: plLabel,
    diff: plLabel,
    pct: plLabel,
    month: plLabel,
    ytd: plLabel,
    months: z.array(plLabel).length(12),
    outside: plLabel,
    unavailable: plLabel,
    notAvailable: plLabel,
    outsideStructure: plLabel,
    partial: plLabel,
    // Sigilo (v3.1): item que depende de pessoal e soma só do que a pessoa vê.
    restricted: plLabel.default('Indicador indisponível para este perfil de acesso'),
    visibleTotal: plLabel.default('Total visível'),
    // Moeda de apresentação, critério de câmbio e pendências de conversão, já traduzidos.
    conversion: z.string().trim().min(1).max(600).optional(),
  }),
});
export type FpaPlExportInput = z.infer<typeof fpaPlExportInput>;
